import { memo, useState, useCallback, useEffect, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControlLabel, Switch, useTheme, useMediaQuery } from '@mui/material';
import RichTextInput from 'components/RichTextInput/RichTextInput';
import Caption from 'components/Typography/Caption';
import Label from 'components/Typography/Label';
import Heading from 'components/Typography/Heading';
import { getImageUploadStyle, textAreaInputSx } from '../../forms/AssetFormSx';
import { useAssetEditorsContext } from '../../context/AssetEditorsContextProvider';
import { useAssetConfigContext } from '../../context/AssetConfigContextProvider';
import { useFormContext } from 'react-hook-form';
import { ImageUpload, ImgActionType } from 'components/ImageUpload/ImageUpload';
import { getBaseCleanUrl, ImageData } from 'components/ImageUpload/ImageData';
import { SYNC_DELAY_MS } from 'utility/constants';
import { AssetFormValuesType } from '../../context/AssetConfigUtilities';
import { hasPrintPrivilege } from 'utility/privileges';
import { TSessionConfigData } from 'providers/contexts/CosmosContext';

/**
 * check for showing print options
 * - mainly wrapper around hasPrintPrivilege
 */
export function hasPrintOptions(sc?: TSessionConfigData): boolean {
  if (!sc) {
    return false;
  }
  const hasTotalCMS = hasPrintPrivilege(sc);
  return hasTotalCMS;
}

const PRINT_HEADLINE_ENABLED_TYPES = ['article'];
const PRINT_JUMP_KEYWORD_ENABLED_TYPES = ['article'];
const PRINT_PLACEMENT_PRIORITY_ENABLED_TYPES = ['article', 'image', 'collection', 'file'];
const PRINT_IMAGE_ENABLED_TYPES = ['image'];

/**
 * Print Headline and Print Subheadline
 */
export function getPrintHeadlinesEnabled(recordType?: string) {
  if (!recordType) {
    return false;
  }
  return PRINT_HEADLINE_ENABLED_TYPES.includes(recordType);
}

export function getPrintPlacementPriorityEnabled(recordType?: string) {
  if (!recordType) {
    return false;
  }
  return PRINT_PLACEMENT_PRIORITY_ENABLED_TYPES.includes(recordType);
}

export function getPrintJumpKeywordEnabled(recordType?: string) {
  if (!recordType) {
    return false;
  }
  return PRINT_JUMP_KEYWORD_ENABLED_TYPES.includes(recordType);
}

export function getPrintImageEnabled(recordType?: string) {
  if (!recordType) {
    return false;
  }
  return PRINT_IMAGE_ENABLED_TYPES.includes(recordType);
}

type HiResSlotString = 'print-hires-color' | 'print-hires-bw';

/**
 * Print fields component
 */
export const Print = memo(() => {
  const theme = useTheme();
  const fullWidth = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { recordCopy, recordType, isReadOnlyMode, yDoc } = useAssetConfigContext();
  const { setValue, getValues, watch } = useFormContext<AssetFormValuesType>();

  const {
    nxtSyncStore,
    printHeadlineEditor,
    printHeadlineEditorConfig,
    printSubheadlineEditor,
    printSubheadlineEditorConfig,
    printPlacementPriorityEditor,
    printPlacementPriorityEditorConfig,
    printJumpKeywordEditor,
    printJumpKeywordEditorConfig,
    printJumpHeadEditor,
    printJumpHeadEditorConfig,
    printNotesEditor,
    printNotesEditorConfig,
  } = useAssetEditorsContext();

  const { state: syncState } = nxtSyncStore;

  // Conditional rendering
  const showPrintHeadlineFields = useMemo(() => {
    return getPrintHeadlinesEnabled(recordType);
  }, [recordType]);

  const showPrintPlacementPriorityField = useMemo(() => {
    return getPrintPlacementPriorityEnabled(recordType);
  }, [recordType]);

  const showPrintJumpKeywordField = useMemo(() => {
    return getPrintJumpKeywordEnabled(recordType);
  }, [recordType]);

  const showPrintImageField = useMemo(() => {
    return getPrintImageEnabled(recordType);
  }, [recordType]);

  // File slots initialization
  const imgColorFiles = recordCopy?.files?.['print-hires-color'];
  const imgColorPreview = imgColorFiles?.url;
  const imgColorSrc = getBaseCleanUrl(imgColorPreview);
  const imgColorCropValues = imgColorFiles?.view_crop;

  const [imageColorState, setImgColorState] = useState<ImageData>({
    src: imgColorSrc,
    preview: imgColorPreview,
    height: imgColorFiles?.height,
    width: imgColorFiles?.width,
    crop: imgColorCropValues,
  });

  const imgBwFiles = recordCopy?.files?.['print-hires-bw'];
  const imgBwPreview = imgBwFiles?.url;
  const imgBwSrc = getBaseCleanUrl(imgBwPreview);
  const imgBwCropValues = imgBwFiles?.view_crop;

  const [imageBwState, setImgBwState] = useState<ImageData>({
    src: imgBwSrc,
    preview: imgBwPreview,
    height: imgBwFiles?.height,
    width: imgBwFiles?.width,
    crop: imgBwCropValues,
  });

  const handleSetImgDataCbColor = (data: ImageData, action: ImgActionType) => {
    const newState = handleHiResFormVals(data, action, 'print-hires-color');
    setImgColorState(newState);
  };

  const handleSetImgDataCbBw = (data: ImageData, action: ImgActionType) => {
    const newState = handleHiResFormVals(data, action, 'print-hires-bw');
    setImgBwState(newState);
  };

  // These slots are specific to the print-hires image assets
  const handleHiResFormVals = (
    data: ImageData,
    action: ImgActionType,
    slot?: HiResSlotString,
  ): ImageData => {
    try {
      if (action === 'upload') {
        setValue(`files.${slot}.view_crop`, null);
        setValue(`files.${slot}.add_binary`, data.add_binary);
        setValue(`files.${slot}.remove_binary`, undefined);
      }
      if (action === 'removed') {
        setValue(`files.${slot}.view_crop`, null);
        setValue(`files.${slot}.add_binary`, null);
        setValue(`files.${slot}.remove_binary`, data.remove_binary);
      }
      if (action === 'cropped') {
        setValue(`files.${slot}.view_crop`, data.crop);
      }
      // return for state update
      return data;
    } catch (error) {
      console.error('[handleHiResFormVals]:', error);
      return data;
    }
  };

  // Handle Print Ready to SyncedStore
  const handlePrintReadyChange = useCallback(
    (val: any) => {
      const nextVal = Boolean(val?.target?.checked ?? val);
      try {
        yDoc?.transact(() => {
          syncState.syncStoreData.properties ??= {};
          syncState.syncStoreData.properties.print_ready = nextVal;
          setValue('properties.print_ready', nextVal, {
            shouldDirty: true,
            shouldTouch: true,
          });
        });
      } catch (e) {
        console.error('[Print] syncedstore print_ready update failed:', e);
      }
    },
    [setValue, syncState, yDoc],
  );

  // SyncedStore inbound print_ready update to local form
  const syncPrintReady = syncState?.syncStoreData?.properties?.print_ready;
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const formPrintReady = getValues('properties.print_ready');
      if (formPrintReady !== syncPrintReady) {
        setValue('properties.print_ready', syncPrintReady, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }, SYNC_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [setValue, getValues, syncPrintReady]);

  if (
    !printHeadlineEditor ||
    !printHeadlineEditorConfig ||
    !printSubheadlineEditor ||
    !printSubheadlineEditorConfig ||
    !printPlacementPriorityEditor ||
    !printPlacementPriorityEditorConfig ||
    !printJumpKeywordEditor ||
    !printJumpKeywordEditorConfig ||
    !printJumpHeadEditor ||
    !printJumpHeadEditorConfig ||
    !printNotesEditor ||
    !printNotesEditorConfig
  ) {
    return null;
  }

  return (
    <Box
      id="Print"
      sx={{ paddingTop: '10px' }} // Temporary adjustment to align drawer arrow. Can remove padding after CMSADMIN-346
    >
      <Heading>{t('blox.editorial.metadata.group.print.label')}</Heading>
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(watch('properties.print_ready'))}
            onChange={handlePrintReadyChange}
            disabled={isReadOnlyMode}
            data-testid="print-ready-toggle"
          />
        }
        label={t('blox.editorial.metadata.group.print.ready.label')}
      />

      {showPrintImageField && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
          }}
        >
          <Box id="print-image-color">
            <Label>
              {t('blox.editorial.metadata.group.print.printImageColor.label')}
            </Label>
            <Caption>
              {t('blox.editorial.metadata.group.print.printImageColor.description')}
            </Caption>

            <ImageUpload
              showCrop={true}
              imgDataState={imageColorState}
              setImgDataCb={handleSetImgDataCbColor}
              fullWidth={fullWidth}
              sx={getImageUploadStyle(fullWidth)}
              showRemove={true}
              disabled={isReadOnlyMode}
            />
          </Box>
          <Box id="print-image-blackwhite">
            <Label>
              {t('blox.editorial.metadata.group.print.printImageBlackWhite.label')}
            </Label>
            <Caption>
              {t('blox.editorial.metadata.group.print.printImageBlackWhite.description')}
            </Caption>
            <ImageUpload
              showCrop={true}
              imgDataState={imageBwState}
              setImgDataCb={handleSetImgDataCbBw}
              fullWidth={fullWidth}
              sx={getImageUploadStyle(fullWidth)}
              showRemove={true}
              disabled={isReadOnlyMode}
            />
          </Box>
        </Box>
      )}

      {showPrintHeadlineFields && (
        <Fragment>
          <Label>{t('blox.editorial.metadata.group.print.headline.label')}</Label>
          <Caption>
            {t('blox.editorial.metadata.group.print.headline.description')}
          </Caption>
          <RichTextInput
            editor={printHeadlineEditor}
            id={printHeadlineEditorConfig?.id}
            name={printHeadlineEditorConfig?.name}
            type={printHeadlineEditorConfig?.type}
            showCount={printHeadlineEditorConfig?.showCount}
          />

          <Label>{t('blox.editorial.metadata.group.print.subheadline.label')}</Label>
          <Caption>
            {t('blox.editorial.metadata.group.print.subheadline.description')}
          </Caption>
          <RichTextInput
            editor={printSubheadlineEditor}
            id={printSubheadlineEditorConfig?.id}
            name={printSubheadlineEditorConfig?.name}
            type={printSubheadlineEditorConfig?.type}
            showCount={printSubheadlineEditorConfig?.showCount}
          />
        </Fragment>
      )}

      {showPrintPlacementPriorityField && (
        <Fragment>
          <Label>{t('blox.editorial.metadata.group.print.printpriority.label')}</Label>
          <Caption>
            {t('blox.editorial.metadata.group.print.printpriority.description')}
          </Caption>
          <RichTextInput
            editor={printPlacementPriorityEditor}
            id={printPlacementPriorityEditorConfig?.id}
            name={printPlacementPriorityEditorConfig?.name}
            type={printPlacementPriorityEditorConfig?.type}
          />
        </Fragment>
      )}

      {showPrintJumpKeywordField && (
        <Fragment>
          <Label>{t('blox.editorial.metadata.group.print.jumpkeyword.label')}</Label>
          <Caption>
            {t('blox.editorial.metadata.group.print.jumpkeyword.description')}
          </Caption>
          <RichTextInput
            editor={printJumpKeywordEditor}
            id={printJumpKeywordEditorConfig?.id}
            name={printJumpKeywordEditorConfig?.name}
            type={printJumpKeywordEditorConfig?.type}
          />
          <Label>{t('blox.editorial.metadata.group.print.jumphead.label')}</Label>
          <Caption>
            {t('blox.editorial.metadata.group.print.jumphead.description')}
          </Caption>
          <RichTextInput
            editor={printJumpHeadEditor}
            id={printJumpHeadEditorConfig?.id}
            name={printJumpHeadEditorConfig?.name}
            type={printJumpHeadEditorConfig?.type}
          />
        </Fragment>
      )}

      <Label>{t('blox.editorial.metadata.group.print.notes.label')}</Label>
      <Caption>{t('blox.editorial.metadata.group.print.notes.description')}</Caption>
      <RichTextInput
        editor={printNotesEditor}
        id={printNotesEditorConfig?.id}
        name={printNotesEditorConfig?.name}
        type={printNotesEditorConfig?.type}
        showCount={printNotesEditorConfig?.showCount}
        sx={textAreaInputSx}
      />
    </Box>
  );
});
Print.displayName = 'Print';
